import React, { useEffect, useState } from 'react';
import styles from './Styles/Defaults.module.css';
import {
  getPrices,
  setPrices,
  updatePrices,
} from '../../Services/arndAPI';
import Loader from '../../Components/Loader/Loader';
import PageHeader from '../../Components/PageHeader/PageHeader';
import Page from '../../Components/Page/Page';

// Prices -> Defaults (SCRUM-334): what this page manages moved onto each
// event at create time — the Prices row survives only as the pre-fill
// copied onto a new event; editing it here never touches existing events.
export default function Defaults({ user }) {
  const [imagePrice, setImagePrice] = useState('');
  const [albumPrice, setAlbumPrice] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [expirationTime, setExpirationTime] = useState('');
  const [matchThreshold, setMatchThreshold] = useState('95'); // SCRUM-338
  const [minAppVersion, setMinAppVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [hasPrices, setHasPrices] = useState(false);

  const fetchPrices = async () => {
    setLoading(true);
    setStatus('');
    try {
      const data = await getPrices();
      setImagePrice(data.imagesPrice);
      setAlbumPrice(data.albumPrice);
      setDiscountAmount(data.discountAmount);
      setExpirationTime(data.expirationTime || '');
      setMatchThreshold(String(data.matchThreshold ?? 95));
      // Not shipped by the API yet (SCRUM-334 in progress) — fall back to empty.
      setMinAppVersion(data.minAppVersion ?? '');
      setHasPrices(true);
    } catch (err) {
      setStatus('No defaults found. You can create them.');
      setHasPrices(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrices();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('Saving...');
    const payload = {
      imagePrice: parseFloat(imagePrice),
      albumPrice: parseFloat(albumPrice),
      discountAmount: parseFloat(discountAmount),
      expirationTime: parseInt(expirationTime),
      matchThreshold: parseInt(matchThreshold),
      minAppVersion: minAppVersion || undefined,
    };

    try {
      if (hasPrices) {
        await updatePrices(payload);
      } else {
        await setPrices(payload);
      }
      setStatus('Defaults saved successfully.');
      setHasPrices(true);
    } catch (err) {
      setStatus('Failed to save defaults.');
    }
  };

  return (
    <Page narrow>
      <PageHeader title="Defaults" subtitle="Applied to new events — edit an event to change it there." />

      {loading ? (
        <Loader />
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="imagePrice">Default photo price</label>
            <input
              id="imagePrice"
              type="number"
              step="0.01"
              value={imagePrice}
              onChange={(e) => setImagePrice(e.target.value)}
              required
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="albumPrice">Default album price</label>
            <input
              id="albumPrice"
              type="number"
              step="0.01"
              value={albumPrice}
              onChange={(e) => setAlbumPrice(e.target.value)}
              required
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="discountAmount">Default survey discount (%)</label>
            <input
              id="discountAmount"
              type="number"
              step="0.01"
              value={discountAmount}
              onChange={(e) => setDiscountAmount(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="expirationTime">Default sales window after the event (days)</label>
            <input
              id="expirationTime"
              type="number"
              value={expirationTime}
              onChange={(e) => setExpirationTime(e.target.value)}
              required
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="matchThreshold">Default match threshold</label>
            <input
              id="matchThreshold"
              type="number"
              min="89"
              max="100"
              value={matchThreshold}
              onChange={(e) => setMatchThreshold(e.target.value)}
              required
            />
            <div className={styles.hint}>
              Auto matches at or above this score are visible to parents; 89–{Number(matchThreshold) ? Number(matchThreshold) - 1 : 94} go to
              Review on the ops page, along with any match held for another reason — an adult face, a face too small next to the main
              athlete, or eyes closed. Applies within 30 seconds.
            </div>
          </div>

          <div className={styles.field}>
            <label htmlFor="minAppVersion">Minimum app version</label>
            <input
              id="minAppVersion"
              type="text"
              placeholder="e.g. 1.2.1"
              value={minAppVersion}
              onChange={(e) => setMinAppVersion(e.target.value)}
            />
            <div className={styles.hint}>
              Builds below this version are blocked with an Update-required screen (iOS and Android compare their app version, e.g. 1.2.1)
            </div>
          </div>

          <button type="submit" className={styles.saveBtn}>Save</button>
          {status && <div className={styles.status}>{status}</div>}
        </form>
      )}
    </Page>
  );
}
